import AddCircleIcon from '@assets/images/icons/add-circle-outlined.svg';
import IndicatorIcon from '@assets/images/icons/indicator-icon.svg';
import { useAppSelector } from '@hooks/useAppSelector';
import { MotionImage, MotionText } from '@shared/atoms';
import classNames from 'classnames';
import React from 'react';
import styles from './index.module.scss';

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

interface RecommendedAreasCardProps {
  priority: {
    id: number;
    category: string;
    title: string;
    score: string;
    scoreClass: string;
    icon: JSX.Element | string | undefined;
    iconUrl: string | null;
    subCategory?: any;
  };
  isSelected: boolean;
  handleRemovePriority: (id: number) => void;
  handleAddPriority: any;
  className?: string;
  'aria-labelledby'?: string;
  alignment?: boolean;
}

export const RecommendedAreasCard: React.FC<RecommendedAreasCardProps> = ({
  priority,
  isSelected,
  className,
  handleRemovePriority,
  handleAddPriority,
  'aria-labelledby': ariaLabelledBy,
  alignment,
}) => {
  const {
    questionnaire,
    answers: stateAnswers,
    currentQuestionIndex,
  } = useAppSelector((state) => state.questionnaireV2 || {});

  const questions = questionnaire?.questionnairesV2Questions;

  const extractBaseClass = (hashedClass: string) => {
    // Assuming format like "FocusAreas_good__9qhv6"
    const parts = hashedClass.split('_');

    return parts.length > 1 ? parts[1] : hashedClass;
  };

  return (
    <div
      className={`${styles.priorityCard} ${isSelected ? styles.selectedPriorityCard : ''}`}
      onClick={() => handleAddPriority(priority)}
    >
      <div className={styles.priorityHeader}>
        <div className={styles.priorityInfo}>
          <div className={styles.icon}>
            {priority.icon ? (
              priority.icon
            ) : priority.iconUrl ? (
              <MotionImage
                src={priority.iconUrl}
                alt={`${priority.title} icon`}
                aria-hidden="true"
              />
            ) : (
              <MotionImage src={IndicatorIcon} alt={`${priority.title} icon`} aria-hidden="true" />
            )}
          </div>
          <div>
            {priority?.category !== 'Indicator' && (
              // {/* Assessment > Category */}
              <MotionText
                as="text"
                text={
                  priority.subCategory
                    ? `${priority.title} > ${priority.subCategory}`
                    : priority.title
                }
                className={styles.categoryLabel}
              />
            )}
            {priority?.category === 'Indicator' && (
              <MotionText as="h4" text={priority.title} className={styles.categoryLabel} />
            )}

            {/* Label from the question table */}
            <MotionText as="h4" text={priority.category} className={styles.priorityTitle} />
          </div>
        </div>

        {isSelected ? (
          <div className={styles.checkedArea}>
            <div className={`${styles.checkButton} ${alignment ? styles.alignment : ''}`}>
              <CheckIcon />{' '}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemovePriority(priority.id);
              }}
              aria-label={`Remove ${priority.title} from priorities`}
              className={`${styles.removeContainer}`}
            >
              <i
                className={classNames(
                  'icon',
                  'icon-x',
                  `${styles.btnRemoveIcon} ${alignment ? styles.alignment : ''}`,
                )}
              />
            </button>
          </div>
        ) : (
          <button
            className={styles.addButton}
            onClick={(e) => {
              e.stopPropagation();
              handleAddPriority(priority);
            }}
            aria-label={`Add ${priority.title} to priorities`}
          >
            <MotionImage src={AddCircleIcon} alt="" className={styles.addIcon} />
            &nbsp; <span>Add</span>
          </button>
        )}
      </div>

      <div
        className={`${styles.scoreLabel} ${styles[extractBaseClass(priority.scoreClass)]} ${
          alignment ? styles.alignment : ''
        }`}
      >
        {priority.score === 'Moderate' ? 'Moderate' : `Your score: ${priority.score}`}
      </div>
    </div>
  );
};

RecommendedAreasCard.defaultProps = {
  className: styles.answersContainer,
  'aria-labelledby': undefined,
  alignment: false,
};
